// Expansión mágica desde el lienzo — ADR-2026-09-16-las-herramientas-del-lienzo-abren-en-panel.
//
// El sheet es `ExpansionMagicaSheet` (presentacional a propósito: cada superficie pone
// el guard, la llamada y qué hacer con la imagen). Acá: guard de saldo, `expandirImagen`
// y la imagen nueva al lienzo, que la cuelga debajo de la original.

import { useEffect, useRef, useState } from 'react';
import ExpansionMagicaSheet from '../../../components/ExpansionMagicaSheet';
import { CREDITOS_EXPANSION, expandirImagen, type RatioExpansion } from '../../../lib/expansionMagica';
import { GenerateAdError } from '../../../lib/generateAdRequest';
import { ratioDeFormato } from '../../../utils/ratioDeFormato';
import type { PropsDeHerramienta } from './herramienta';
import { consultarSaldo, tipoDeLimite } from './saldo';
import { useUrlDelOriginal } from './useUrlDelOriginal';

export const PanelExpansion: React.FC<PropsDeHerramienta> = ({
  celda,
  ratio,
  onCerrar,
  onImagenNueva,
  onLimite,
  onError,
}) => {
  const url = useUrlDelOriginal(celda);
  const [listo, setListo] = useState(false);
  const [procesando, setProcesando] = useState(false);
  // El plan que leyó el guard, para el modal de límite si es el backend el que rechaza.
  const planRef = useRef<string | null>(null);
  useEffect(() => {
    let vivo = true;
    void consultarSaldo(celda.companyId).then((s) => {
      if (!vivo) return;
      planRef.current = s.plan;
      if (s.restantes !== null && s.restantes < CREDITOS_EXPANSION) {
        onLimite(tipoDeLimite(s.plan));
        onCerrar();
        return;
      }
      setListo(true);
    });
    return () => {
      vivo = false;
    };
  }, [celda.companyId, onCerrar, onLimite]);

  const expandir = async (r: RatioExpansion) => {
    if (procesando) return;
    setProcesando(true);
    try {
      const expandida = await expandirImagen({
        imagenId: celda.imageId,
        aspectRatio: r,
        companyId: celda.companyId,
        modoUtilizado: 'expansion_magica_lienzo',
      });
      onImagenNueva({
        id: expandida.id,
        origenId: celda.imageId,
        ratio: ratioDeFormato(r) ?? ratio,
        producto: celda.titulo,
      });
      onCerrar();
    } catch (err) {
      if (err instanceof GenerateAdError && err.errorType === 'billing') {
        onLimite(tipoDeLimite(planRef.current));
        onCerrar();
      } else {
        onError(err instanceof Error ? err.message : 'No pudimos expandir la imagen. Intentá de nuevo.');
      }
    } finally {
      setProcesando(false);
      try {
        window.dispatchEvent(new Event('atlas:credits-updated'));
      } catch {}
    }
  };

  return (
    <ExpansionMagicaSheet
      abierto={listo && !!url}
      imagenUrl={url ?? ''}
      procesando={procesando}
      onCerrar={onCerrar}
      onExpandir={(r) => void expandir(r)}
    />
  );
};
